import dayjs from "dayjs";

import { serverResponseFormat } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import type { TotalsData } from "app/pages/keyword-research/keyword-analysis/pages/monitor-lists/graphs/share-of-voice/types";
import type ColorStack from "app/components/colorsStack/ColorStack";
import type {
    ITrafficData,
    ITransformedDataPoint,
    ITrendsData,
} from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/clicks-over-time-graph/types";

export const DEFAULT_COLOR = colorsPalettes.carbon[200];

const areAllClicksZero = (domains: ITrendsData): boolean => {
    return Object.values(domains).every((domain) =>
        Object.values(domain.SiteTrends).every((date) => date.Clicks === 0),
    );
};

const transformData = (
    response: ITrafficData,
    colorStack: ColorStack,
    hasPartialWeek: boolean,
): {
    graphDataItems: ITransformedDataPoint[];
    totalsData: TotalsData;
} => {
    const totalsData = response.Totals || {};
    const trendsData = response.Trends;

    if (areAllClicksZero(trendsData)) {
        return { graphDataItems: [], totalsData };
    }

    colorStack.reset();

    const transformedData: ITransformedDataPoint[] = Object.entries(trendsData).reduce(
        (items, [domain, siteTrendsData]) => {
            let total = 0;
            const data = Object.entries(siteTrendsData.SiteTrends)
                .map(([date, clickMetrics], index, arr) => {
                    const timestampValue = dayjs.utc(date, serverResponseFormat).valueOf();
                    const isPartial = index === arr.length - 1 ? hasPartialWeek : false;
                    total += clickMetrics.Clicks;
                    return {
                        x: timestampValue,
                        y: clickMetrics.Clicks,
                        isPartial,
                        shareValue: clickMetrics.ClicksShare,
                    };
                })
                .sort((a, b) => a.x - b.x);

            const item = {
                name: domain,
                id: domain,
                data,
                marker: { symbol: "circle" },
                visible: false,
                isTotal: false,
                color: DEFAULT_COLOR,
                total,
                zones: [],
            };
            return [item, ...items];
        },
        [] as ITransformedDataPoint[],
    );

    const transformedDataSorted = transformedData.sort((a, b) => b.total - a.total);

    return { graphDataItems: transformedDataSorted, totalsData };
};

export default transformData;
